import React from 'react';

import { Box, Container } from '../Box';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Link } from '../Link';
import { Logo } from '../Logo';
import { space } from '../shared/styles';
import { Text } from '../Typography';
import {
  avatarPlaceholderStyles,
  centeredLogoStyles,
  containerStyles,
  desktopLogoStyles,
  desktopNavStyles,
  hamburgerButtonStyles,
  logoStyles,
  marketingContainerStyles,
  marketingDesktopNavStyles,
  marketingNavLinksStyles,
  marketingNavLinkStyles,
  navigationMarketingStyles,
  navigationStyles,
  navLinksStyles,
  navLinkStyles,
} from './Navigation.styles';
import { type NavigationProps } from './Navigation.types';

const Navigation = ({
  variant = 'default',
  onMenuToggle,
  className,
  logoVariant = 'standard',
  logoTheme = 'light',
  logoHref = '/',
  linkAs = 'a',
  navLinks = [
    { label: 'Properties', href: '/properties' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ],
  cta,
  showAvatar,
  onAvatarClick,
  ...rest
}: NavigationProps) => {
  const isMarketing = variant === 'marketing';
  const shouldShowAvatar = showAvatar ?? !isMarketing;

  const logo = (
    <Link as={linkAs} href={logoHref} underline="none" aria-label="Home">
      <Logo variant={logoVariant} theme={logoTheme} />
    </Link>
  );

  const mobileLogo = (
    <Link as={linkAs} href={logoHref} underline="none" aria-label="Home">
      <Logo variant={logoVariant} theme={logoTheme} size={8} css={logoStyles} />
    </Link>
  );

  const navContent = (
    <Box
      display="flex"
      alignItems="center"
      padding={space[4]}
      position="relative"
      css={isMarketing ? marketingContainerStyles : containerStyles}
    >
      {/* Mobile: Hamburger Menu Button */}
      <Box
        as="button"
        onClick={onMenuToggle}
        display="flex"
        alignItems="center"
        justifyContent="center"
        backgroundColor="transparent"
        border="none"
        padding={space[2]}
        css={hamburgerButtonStyles}
        aria-label="Toggle menu"
      >
        <Icon variant="Bars" size="large" />
      </Box>

      {/* Desktop: Logo on the left */}
      <Box css={desktopLogoStyles}>{logo}</Box>

      {/* Mobile: Logo centered */}
      <Box position="absolute" left="50%" top="50%" css={centeredLogoStyles}>
        {mobileLogo}
      </Box>

      {/* Desktop: Navigation Links, CTA, and Avatar */}
      <Box css={isMarketing ? marketingDesktopNavStyles : desktopNavStyles}>
        <Box as="nav">
          <Box
            as="ul"
            css={isMarketing ? marketingNavLinksStyles : navLinksStyles}
          >
            {navLinks.map((link) => (
              <Box as="li" key={link.href}>
                {isMarketing ? (
                  <Link
                    as={linkAs}
                    href={link.href}
                    onClick={link.onClick}
                    underline="none"
                    css={marketingNavLinkStyles}
                  >
                    <Text as="span" size="sm" fontWeight="bold">
                      {link.label}
                    </Text>
                  </Link>
                ) : (
                  <a
                    href={link.href}
                    onClick={link.onClick}
                    css={navLinkStyles}
                  >
                    {link.label}
                  </a>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {isMarketing && cta && (
          <Button
            as={cta.href ? 'a' : 'button'}
            href={cta.href}
            onClick={cta.onClick}
            variant="primary"
            size="md"
          >
            {cta.label}
          </Button>
        )}

        {shouldShowAvatar && (
          <Box
            as="button"
            onClick={onAvatarClick}
            backgroundColor="transparent"
            border="none"
            css={avatarPlaceholderStyles}
            aria-label="User menu"
          >
            U
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      css={isMarketing ? navigationMarketingStyles : navigationStyles}
      className={className}
      {...rest}
    >
      {isMarketing ? <Container>{navContent}</Container> : navContent}
    </Box>
  );
};

export default Navigation;
